import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiPackage,
  FiDollarSign,
  FiClock,
  FiBox,
  FiInfo,
  FiLayers,
  FiCheckCircle
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useFormulaciones } from '../../hooks/useFormulaciones';
import { productosService } from '../../services';

const UNIT_TO_BASE = {
  KG: 1000, G: 1, MG: 0.001, MCG: 0.000001,
  L: 1000, ML: 1,
  UNIT: 1, BOTTLE: 1, BOX: 1, PACKAGE: 1,
};

const FormulacionDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    getFormulacionById,
  } = useFormulaciones();

  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capsuleIngredientCost, setCapsuleIngredientCost] = useState(0);
  const [labelProductCost, setLabelProductCost] = useState(0);

  useEffect(() => {
    if (id) {
      loadFormulacion();
    }
  }, [id]);

  const loadFormulacion = async () => {
    setIsLoading(true);
    try {
      const result = await getFormulacionById(id);
      if (result.success) {
        setFormData(result.data);

        // Resolver costo del ingrediente cápsula (la API no lo devuelve)
        if (result.data.capsule_type && result.data.capsule_type !== 'NOT_APPLICABLE' && result.data.capsule_ingredient_id) {
          const capRes = await productosService.getProducto(result.data.capsule_ingredient_id);
          if (capRes.success && capRes.data) {
            setCapsuleIngredientCost(parseFloat(capRes.data.cost_price) || 0);
          } else {
            setCapsuleIngredientCost(0);
          }
        } else {
          setCapsuleIngredientCost(0);
        }

        // Resolver costo del material de etiqueta
        if (result.data.label_printer && result.data.label_printer !== 'NO_LABEL' && result.data.label_product_id) {
          const labRes = await productosService.getProducto(result.data.label_product_id);
          if (labRes.success && labRes.data) {
            setLabelProductCost(parseFloat(labRes.data.cost_price) || 0);
          } else {
            setLabelProductCost(0);
          }
        } else {
          setLabelProductCost(0);
        }
      } else {
        setError(result.message || 'Error al cargar formulación');
      }
    } catch (error) {
      setError('Error de conexión al cargar formulación');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    if (formData && formData.result_product_id) {
      navigate(`/system/productos/${formData.result_product_id}/editar`);
    } else {
      navigate(`/system/formulaciones/${id}/editar`);
    }
  };

  const handleBack = () => {
    navigate('/system/formulaciones');
  };

  const isDiscreteUnit = useCallback(() => {
    if (!formData) return true;
    const unit = formData.quantity_unit;
    return unit === 'UNIT';
  }, [formData]);

  const calculateTotals = useCallback(() => {
    if (!formData || !formData.ingredients) {
      return { ingredientCost: 0, containerCost: 0, lidCost: 0, capsuleCost: 0, labelCost: 0, laborCost: 0, totalCost: 0, costPerUnit: 0 };
    }

    const effectiveQpc = isDiscreteUnit()
      ? parseFloat(formData.quantity_per_container || 1)
      : 1;

    // Costo de ingredientes por envase
    let ingredientCost = 0;
    formData.ingredients.forEach(ing => {
      const qty = parseFloat(ing.quantity_per_unit) || 0;
      let unitCost = parseFloat(ing.cost_per_unit) || 0;

      // Si no hay cost_per_unit pero hay cost_price del producto ingrediente, convertir unidades
      if (!unitCost && ing.cost_price) {
        const costPrice = parseFloat(ing.cost_price);
        const baseUnit = ing.base_unit || ing.unit;
        const formulaUnit = ing.unit || 'G';
        const factorBase = UNIT_TO_BASE[baseUnit] || 1;
        const factorFormula = UNIT_TO_BASE[formulaUnit] || 1;
        unitCost = costPrice * (factorFormula / factorBase);
      }

      ingredientCost += qty * unitCost * effectiveQpc;
    });

    // Costos adicionales
    const containerCost = formData.container_product_cost ? parseFloat(formData.container_product_cost) : 0;
    const lidCost = formData.lid_product_cost ? parseFloat(formData.lid_product_cost) : 0;
    const laborCost = parseFloat(formData.labor_cost_per_hour) || 0;

    // Costo del ingrediente cápsula (resuelto desde productosService)
    const capsuleCost = (formData.capsule_type && formData.capsule_type !== 'NOT_APPLICABLE')
      ? capsuleIngredientCost * effectiveQpc
      : 0;

    // Costo del material de etiqueta
    const labelCost = (formData.label_printer && formData.label_printer !== 'NO_LABEL')
      ? labelProductCost
      : 0;

    const totalCost = ingredientCost + containerCost + lidCost + capsuleCost + labelCost + laborCost;
    const costPerUnit = isDiscreteUnit() && formData.quantity_per_container > 0
      ? totalCost / parseFloat(formData.quantity_per_container)
      : totalCost;

    return { ingredientCost, containerCost, lidCost, capsuleCost, labelCost, laborCost, totalCost, costPerUnit };
  }, [formData, isDiscreteUnit, capsuleIngredientCost, labelProductCost]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getCapsuleTypeLabel = (type) => {
    const types = {
      'VEGETARIAN': 'Vegetarianas',
      'GELATIN': 'Gelatina',
      'NOT_APPLICABLE': 'No Aplica'
    };
    return types[type] || type || 'No Aplica';
  };

  const getFormulaTypeLabel = (type) => {
    const types = {
      'BETEL': 'Betel Natural',
      'NATURALANDLAB': 'Natural & Lab',
      'COTIZACIONES': 'Cotizaciones',
      'EXCLUSIVE': 'Exclusiva'
    };
    return types[type] || type;
  };

  const getUnitLabel = (unit) => {
    const units = {
      'UNIT': 'Unidades', 'G': 'Gramos', 'KG': 'Kilogramos',
      'MG': 'Miligramos', 'ML': 'Mililitros', 'L': 'Litros'
    };
    return units[unit] || unit;
  };

  const getLabelPrinterLabel = (value) => {
    const printers = {
      'EN_BETEL': 'EN BETEL',
      'EXTERNAL': 'EXTERNA',
      'NO_LABEL': 'SIN ETIQUETA'
    };
    return printers[value] || value;
  };

  const getEffectiveQpc = useCallback(() => {
    if (!formData) return 1;
    return isDiscreteUnit() ? (parseFloat(formData.quantity_per_container) || 1) : 1;
  }, [formData, isDiscreteUnit]);

  const formatNum = (num, maxDecimals = 2) => {
    if (num == null || isNaN(num)) return '0';
    const rounded = parseFloat(parseFloat(num).toFixed(maxDecimals));
    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
    });
  };

  const formatWeight = (grams) => {
    if (grams >= 1000) return `${formatNum(grams / 1000)} kg`;
    return `${formatNum(grams)} g`;
  };

  const formatVolume = (ml) => {
    if (ml >= 1000) return `${formatNum(ml / 1000)} l`;
    return `${formatNum(ml)} ml`;
  };

  const getTotalPerUnit = useCallback(() => {
    let totalGrams = 0;
    let totalML = 0;
    if (!formData || !formData.ingredients) return { grams: 0, ml: 0 };

    formData.ingredients.forEach(ing => {
      const amount = parseFloat(ing.quantity_per_unit) || 0;
      const unit = ing.unit;
      if (unit === 'KG') totalGrams += amount * 1000;
      else if (unit === 'G') totalGrams += amount;
      else if (unit === 'MG') totalGrams += amount / 1000;
      else if (unit === 'MCG') totalGrams += amount / 1000000;
      else if (unit === 'L') totalML += amount * 1000;
      else if (unit === 'ML') totalML += amount;
    });
    return { grams: totalGrams, ml: totalML };
  }, [formData]);

  const getProductionAmount = (quantity) => {
    const totals = getTotalPerUnit();
    const effectiveQpc = getEffectiveQpc();
    const totalGrams = totals.grams * effectiveQpc * quantity;
    const totalML = totals.ml * effectiveQpc * quantity;

    if (totalGrams > 0 && totalML > 0) {
      return (
        <div style={{ lineHeight: '1.4' }}>
          <div>{formatWeight(totalGrams)}</div>
          <div>{formatVolume(totalML)}</div>
        </div>
      );
    }
    if (totalGrams > 0) return formatWeight(totalGrams);
    if (totalML > 0) return formatVolume(totalML);
    return '-';
  };

  const getIngredientProductionAmount = (ingredient, quantity) => {
    const amountPerUnit = parseFloat(ingredient.quantity_per_unit) || 0;
    if (amountPerUnit === 0) return '-';
    const effectiveQpc = getEffectiveQpc();
    const total = amountPerUnit * effectiveQpc * quantity;
    const unit = ingredient.unit;

    switch (unit) {
      case 'KG': return `${formatNum(total)} kg`;
      case 'G': return formatWeight(total);
      case 'MG': {
        const g = total / 1000;
        if (g >= 1000) return `${formatNum(g / 1000)} kg`;
        if (g >= 1) return `${formatNum(g)} g`;
        return `${formatNum(total)} mg`;
      }
      case 'MCG': {
        const mg = total / 1000;
        const g = total / 1000000;
        if (g >= 1000) return `${formatNum(g / 1000)} kg`;
        if (g >= 1) return `${formatNum(g)} g`;
        if (mg >= 1) return `${formatNum(mg)} mg`;
        return `${formatNum(total, 0)} mcg`;
      }
      case 'L': return `${formatNum(total)} l`;
      case 'ML': return formatVolume(total);
      case 'UNIT': return `${formatNum(total, 0)} unid`;
      default: return `${formatNum(total)} ${unit || ''}`;
    }
  };

  const getProductionColumns = useCallback(() => {
    const fromApi = (formData?.production_quantities || [])
      .map(pq => parseInt(pq.quantity, 10))
      .filter(q => Number.isFinite(q) && q > 0);
    const base = fromApi.length > 0 ? fromApi : [250, 500, 1000, 2000];
    return base.slice(0, 4);
  }, [formData]);

  const getQpcLabel = () => {
    if (!formData) return '';
    const qpc = formData.quantity_per_container || 0;
    const unit = formData.quantity_unit || 'UNIT';
    if (unit === 'UNIT') return `${qpc} unid/envase`;
    return `${qpc} ${unit.toLowerCase()}/envase`;
  };

  const formatTotalPerUnit = () => {
    if (!formData) return '-';
    const { grams, ml } = getTotalPerUnit();
    if (grams > 0 && ml > 0) {
      const weightStr = grams >= 1000 ? `${(grams / 1000).toFixed(3)} kg` : `${grams.toFixed(1)} g`;
      const volumeStr = ml >= 1000 ? `${(ml / 1000).toFixed(3)} l` : `${ml.toFixed(1)} ml`;
      return (
        <div style={{ lineHeight: '1.4' }}>
          <div>{weightStr} (peso)</div>
          <div>{volumeStr} (volumen)</div>
        </div>
      );
    }
    if (grams > 0) return grams >= 1000 ? `${(grams / 1000).toFixed(3)} kg` : `${grams.toFixed(1)} g`;
    if (ml > 0) return ml >= 1000 ? `${(ml / 1000).toFixed(3)} l` : `${ml.toFixed(1)} ml`;
    return '-';
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando formulación..." />;
  }

  if (error || !formData) {
    return (
      <div className="factura-form-page">
        <div className="alert alert--danger">
          <strong>Error:</strong> {error || 'No se pudo cargar la formulación'}
        </div>
        <button className="btn btn--outline" onClick={handleBack}>
          <FiArrowLeft size={16} />
          Volver
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleBack}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {formData.formula_name || `Formulación #${id}`}
        </h1>

        <div className="header-actions">
          <button className="btn btn--primary" onClick={handleEdit}>
            <FiEdit size={16} />
            Editar
          </button>
        </div>
      </div>

      {/* Card 1: Información Principal */}
      <div className="invoice-header-card">
        <div className="invoice-header-card__left">
          <div className="invoice-icon-circle">
            <FiLayers size={32} />
          </div>
          <div className="invoice-header-info">
            <h3 className="invoice-header-title">
              {formData.formula_name}
            </h3>
            <p className="invoice-header-subtitle">
              {formData.company_name} • {getFormulaTypeLabel(formData.formula_type)}
            </p>
          </div>
        </div>

        <div className="invoice-header-card__grid">
          {/* Con Origen presente, los items de col 2 se desplazan una fila hacia abajo
              para que "Origen" quede justo debajo de "Fórmula". */}
          {(() => { const hasOrigen = !!formData.source_formula_name; const c2 = (n) => hasOrigen ? n + 1 : n; return (
          <>
          {/* === FILA 1: Compañía | Fórmula === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 1 }}>
            <div className="invoice-field-icon"><FiBox size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Compañía</label>
              <div className="invoice-field-value">{formData.company_name}</div>
            </div>
          </div>
          <div className="invoice-field" style={{ gridColumn: 2, gridRow: 1 }}>
            <div className="invoice-field-icon"><FiLayers size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Fórmula</label>
              <div className="invoice-field-value">{getFormulaTypeLabel(formData.formula_type)}</div>
            </div>
          </div>

          {/* Origen (debajo de Fórmula, solo si la fórmula fue duplicada) */}
          {hasOrigen && (
            <div className="invoice-field" style={{ gridColumn: 2, gridRow: 2 }}>
              <div className="invoice-field-icon"><FiLayers size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Origen</label>
                <div className="invoice-field-value" style={{ color: '#0066cc', fontWeight: 500 }}>
                  Duplicada de: {formData.source_formula_name}
                </div>
              </div>
            </div>
          )}

          {/* === FILA 2: Tipo de Envase | Tiempo para Mezclar === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 2 }}>
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tipo de Envase</label>
              <div className="invoice-field-value">{formData.container_product_name || 'No asignado'}</div>
            </div>
          </div>
          <div className="invoice-field" style={{ gridColumn: 2, gridRow: c2(2) }}>
            <div className="invoice-field-icon"><FiClock size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tiempo para Mezclar</label>
              <div className="invoice-field-value">{formData.mixing_time_minutes || 0} minutos</div>
            </div>
          </div>

          {/* === FILA 3: Tapadera | Costo Mano de Obra === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 3 }}>
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tapadera</label>
              <div className="invoice-field-value">{formData.lid_product_name || 'No asignada'}</div>
            </div>
          </div>
          <div className="invoice-field" style={{ gridColumn: 2, gridRow: c2(3) }}>
            <div className="invoice-field-icon"><FiDollarSign size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Costo Mano de Obra</label>
              <div className="invoice-field-value">{formatCurrency(formData.labor_cost_per_hour || 0)}</div>
            </div>
          </div>

          {/* === FILA 4: Cantidad por Envase | Duración de Mejor Uso === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 4 }}>
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Cantidad por Envase</label>
              <div className="invoice-field-value">{formData.quantity_per_container} {getUnitLabel(formData.quantity_unit)}</div>
            </div>
          </div>
          <div className="invoice-field" style={{ gridColumn: 2, gridRow: c2(4) }}>
            <div className="invoice-field-icon"><FiInfo size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Duración de Mejor Uso</label>
              <div className="invoice-field-value">
                {(() => {
                  if (!formData.best_use_date_years) return 'No asignada';
                  const d = new Date(formData.best_use_date_years + 'T00:00:00');
                  if (isNaN(d.getTime())) return formData.best_use_date_years;
                  const now = new Date();
                  const diffMonths = (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
                  const dateStr = d.toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' });
                  return diffMonths > 0 ? `${diffMonths} meses` : dateStr;
                })()}
              </div>
            </div>
          </div>

          {/* === FILA 5: Tipo de Cápsulas | Etiqueta de Impresión === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 5 }}>
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tipo de Cápsulas</label>
              <div className="invoice-field-value">{getCapsuleTypeLabel(formData.capsule_type)}</div>
            </div>
          </div>
          <div className="invoice-field" style={{ gridColumn: 2, gridRow: c2(5) }}>
            <div className="invoice-field-icon"><FiInfo size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Etiqueta de Impresión o Envase</label>
              <div className="invoice-field-value">{getLabelPrinterLabel(formData.label_printer) || '-'}</div>
            </div>
          </div>

          {/* === FILA 6: Ingrediente Cápsula (cond) | Material de Etiqueta (cond) === */}
          {formData.capsule_type && formData.capsule_type !== 'NOT_APPLICABLE' && (
            <div className="invoice-field" style={{ gridColumn: 1, gridRow: 6 }}>
              <div className="invoice-field-icon"><FiPackage size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Ingrediente Cápsula</label>
                <div className="invoice-field-value">{formData.capsule_ingredient_name || 'No asignado'}</div>
              </div>
            </div>
          )}
          {formData.label_printer && formData.label_printer !== 'NO_LABEL' && (
            <div className="invoice-field" style={{ gridColumn: 2, gridRow: c2(6) }}>
              <div className="invoice-field-icon"><FiInfo size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Material de Etiqueta</label>
                <div className="invoice-field-value">{formData.label_product_name || 'No asignado'}</div>
              </div>
            </div>
          )}

          {/* === FILA 7: Medida de Caja | Sílica === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 7 }}>
            <div className="invoice-field-icon"><FiBox size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Medida de Caja para Empacar</label>
              <div className="invoice-field-value">{formData.box_size || 'No asignada'}</div>
            </div>
          </div>
          <div className="invoice-field" style={{ gridColumn: 2, gridRow: c2(7) }}>
            <div className="invoice-field-icon"><FiCheckCircle size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Sílica</label>
              <div className="invoice-field-value">
                {formData.includes_silica ? `SI${formData.silica_cost ? ` - ${formatCurrency(formData.silica_cost)}` : ''}` : 'NO'}
              </div>
            </div>
          </div>

          {/* === FILA 8: Piezas por Caja === */}
          <div className="invoice-field" style={{ gridColumn: 1, gridRow: 8 }}>
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Piezas por Caja</label>
              <div className="invoice-field-value">{formData.pieces_per_box ?? '-'}</div>
            </div>
          </div>
          </>
          ); })()}

          {/* Notas - full width al final */}
          {formData.notes && (
            <div className="invoice-field invoice-field--full" style={{ gridColumn: '1 / -1', gridRow: 9 }}>
              <div className="invoice-field-icon"><FiInfo size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas</label>
                <div className="invoice-field-value">{formData.notes}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card: Tabla de Ingredientes con cantidades de producción */}
      {formData.ingredients && formData.ingredients.length > 0 ? (() => {
        const productionColumns = getProductionColumns();
        const qtyHeader = isDiscreteUnit() ? 'CANT. POR UNIDAD' : 'CANT. POR ENVASE';
        const qtySubHeader = isDiscreteUnit()
          ? 'CÁPSULA'
          : `${formData.quantity_per_container || 0} ${(formData.quantity_unit || 'G').toLowerCase()}`;
        return (
          <div className="invoice-table-card">
            <div className="invoice-table-header">
              <div className="table-header-icon"><FiLayers size={24} /></div>
              <div className="table-header-content">
                <h3 className="table-header-title">Ingredientes de la Formulación</h3>
                <p className="table-header-subtitle">{formData.ingredients.length} ingrediente(s)</p>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '170px' }} rowSpan="2">INGREDIENTES</th>
                    <th style={{ width: '140px' }} rowSpan="2">
                      {qtyHeader}
                      <br />
                      <small style={{ fontWeight: '600', fontSize: '12px', color: '#fff' }}>
                        {qtySubHeader}
                      </small>
                    </th>
                    <th className="text-center" colSpan={productionColumns.length}>
                      CANTIDAD DE PRODUCCIÓN ({getQpcLabel()})
                    </th>
                  </tr>
                  <tr>
                    {productionColumns.map((q, i) => (
                      <th
                        key={`pq-h-${i}`}
                        className="text-center"
                        style={{ width: '110px', padding: '5px' }}
                      >
                        {formatNum(q, 0)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {formData.ingredients.map((ingredient, index) => {
                    const qty = parseFloat(ingredient.quantity_per_unit) || 0;
                    return (
                      <tr key={index}>
                        <td className="ingredient-name" style={{ fontWeight: '500' }}>
                          {ingredient.ingredient_name}
                          {ingredient.base_unit && (
                            <div style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>
                              base: {ingredient.base_unit.toLowerCase()}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{ fontWeight: '500' }}>
                            {qty} {ingredient.unit}
                          </span>
                          {ingredient.base_unit && ingredient.base_unit !== ingredient.unit && (
                            <div style={{ fontSize: '10px', color: '#e67e22', marginTop: '2px', lineHeight: '1.2' }}>
                              ⚠ costo se convierte automáticamente
                            </div>
                          )}
                        </td>
                        {productionColumns.map((q, i) => (
                          <td key={`pq-c-${index}-${i}`} className="text-center production-cell">
                            {getIngredientProductionAmount(ingredient, q)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td className="text-left"><strong>TOTAL INGREDIENTES:</strong></td>
                    <td className="text-center total-per-unit"><strong>{formatTotalPerUnit()}</strong></td>
                    {productionColumns.map((q, i) => (
                      <td key={`pq-t-${i}`} className="text-center total-production">
                        <strong>{getProductionAmount(q)}</strong>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Totales */}
            <div className="invoice-totals-section">
              <div className="totals-container">
                <div className="totals-row">
                  <span className="totals-label">Costo Ingredientes</span>
                  <span className="totals-value">{formatCurrency(totals.ingredientCost)}</span>
                </div>
                {totals.containerCost > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Envase</span>
                    <span className="totals-value">{formatCurrency(totals.containerCost)}</span>
                  </div>
                )}
                {totals.lidCost > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Tapadera</span>
                    <span className="totals-value">{formatCurrency(totals.lidCost)}</span>
                  </div>
                )}
                {totals.capsuleCost > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Ingrediente Cápsula</span>
                    <span className="totals-value">{formatCurrency(totals.capsuleCost)}</span>
                  </div>
                )}
                {totals.labelCost > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Etiqueta</span>
                    <span className="totals-value">{formatCurrency(totals.labelCost)}</span>
                  </div>
                )}
                {totals.laborCost > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Mano de Obra</span>
                    <span className="totals-value">{formatCurrency(totals.laborCost)}</span>
                  </div>
                )}
                <div className="totals-divider"></div>
                <div className="totals-row totals-row--final">
                  <span className="totals-label">Costo Total</span>
                  <span className="totals-value">{formatCurrency(totals.totalCost)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })() : (
        <div className="invoice-empty-state">
          <div className="empty-state-icon"><FiLayers size={64} /></div>
          <h3 className="empty-state-title">No hay ingredientes</h3>
          <p className="empty-state-message">
            Esta formulación no tiene ingredientes registrados
          </p>
        </div>
      )}
    </div>
  );
};

export default FormulacionDetalle;
